import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { superAdminApi } from '@/services/superAdminApi';
import { toast } from 'sonner';
import { DatabaseBackup, Save, PlugZap, Play, RefreshCw, HardDrive, CheckCircle2, XCircle, Trash2, AlertTriangle, Download, Server, RotateCcw, Upload, Boxes } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB pro Chunk (unter dem Proxy-Limit)
const UPLOAD_URL = `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/backup/local/upload`;

const EMPTY = {
  enabled: false, endpoint: '', access_key: '', secret_key: '', bucket: '',
  prefix: 'platform', region: 'us-east-1', retention_days: 30, db_only: true, notify_email: '',
};

export default function BackupPage() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [objects, setObjects] = useState([]);
  const [localTenants, setLocalTenants] = useState([]);
  const [restoreTgt, setRestoreTgt] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadName, setUploadName] = useState('');
  const [fullStatus, setFullStatus] = useState(null);
  const [fullBundles, setFullBundles] = useState([]);
  const [fullRestoreTgt, setFullRestoreTgt] = useState(null);
  const [fullConfirm, setFullConfirm] = useState('');
  const [fullRestoring, setFullRestoring] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const fullPollRef = useRef(null);
  const FULL_CONFIRM = 'KOMPLETTES SYSTEM';

  const loadFull = () => {
    superAdminApi.backupFullStatus().then((r) => setFullStatus(r.data.run)).catch(() => {});
    superAdminApi.backupFullList().then((r) => setFullBundles(r.data.items || [])).catch(() => setFullBundles([]));
  };

  const runFull = async () => {
    try {
      await superAdminApi.backupFullRun({ include_files: true });
      toast.success('Komplett-Backup gestartet – läuft im Hintergrund…');
      setTimeout(loadFull, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Start fehlgeschlagen'); }
  };

  const downloadFull = async (b) => {
    try {
      toast.info('Download startet…');
      const r = await superAdminApi.backupFullDownload(b.filename);
      triggerDownload(r.data, b.filename);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const doFullRestore = async () => {
    if (!fullRestoreTgt) return;
    setFullRestoring(true);
    try {
      await superAdminApi.backupFullRestore({ filename: fullRestoreTgt.filename, confirm: fullConfirm });
      toast.success('Komplett-Wiederherstellung gestartet – läuft im Hintergrund…');
      setFullRestoreTgt(null); setFullConfirm('');
      setTimeout(loadFull, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wiederherstellung fehlgeschlagen');
    } finally { setFullRestoring(false); }
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.tar.gz')) {
      toast.error('Bitte eine .tar.gz Backup-Datei auswählen');
      return;
    }
    const uploadId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    setUploading(true); setUploadPct(0); setUploadName(file.name);

    const postChunk = (i) => {
      const start = i * CHUNK_SIZE;
      const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append('file', blob, `${file.name}.part${i}`);
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('filename', file.name);
      fd.append('offset', String(start));
      return axios.post(UPLOAD_URL, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
    };

    try {
      for (let i = 0; i < totalChunks; i++) {
        let attempt = 0; let r = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try { r = await postChunk(i); break; }
          catch (err) { attempt += 1; if (attempt >= 4) throw err; await new Promise((res) => setTimeout(res, 1000 * attempt)); }
        }
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
        if (r.data.completed) {
          toast.success('Backup hochgeladen – noch NICHT eingespielt!', {
            duration: 12000,
            description: 'Die Datei liegt jetzt in der Liste unten. Klicke beim passenden Studio auf „Wiederherstellen" (↺), um die Daten einzuspielen.',
          });
        }
      }
      loadLocal();
    } catch (e) {
      try { await superAdminApi.backupLocalUploadAbort(uploadId); } catch (_) { /* ignore */ }
      toast.error((e.response?.data?.detail || 'Upload fehlgeschlagen') + ' – bitte erneut versuchen.');
    } finally {
      setUploading(false); setUploadPct(0); setUploadName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const doRestore = async () => {
    if (!restoreTgt) return;
    setRestoring(true);
    try {
      const r = await superAdminApi.backupLocalRestore({ filename: restoreTgt.filename, confirm: restoreConfirm });
      toast.success(`Wiederhergestellt: ${restoreTgt.name}. Sicherung vorher: ${r.data?.safety_backup || '—'}`);
      setRestoreTgt(null); setRestoreConfirm('');
      loadLocal(); loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wiederherstellung fehlgeschlagen');
    } finally { setRestoring(false); }
  };

  const loadConfig = () => superAdminApi.backupConfig().then((r) => {
    setCfg(r.data);
    setForm({ ...EMPTY, ...r.data, secret_key: '' });
  }).catch(() => {});

  const loadStatus = () => superAdminApi.backupStatus().then((r) => setStatus(r.data.run)).catch(() => {});
  const loadList = () => superAdminApi.backupList().then((r) => setObjects(r.data.items || [])).catch(() => setObjects([]));
  const loadLocal = () => superAdminApi.backupLocalList().then((r) => setLocalTenants(r.data.tenants || [])).catch(() => setLocalTenants([]));

  useEffect(() => { loadConfig(); loadStatus(); loadList(); loadLocal(); loadFull(); return () => { clearInterval(pollRef.current); clearInterval(fullPollRef.current); }; }, []);

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadCloud = async (o) => {
    try {
      toast.info('Download wird vorbereitet…');
      const r = await superAdminApi.backupDownloadCloud(o.key);
      triggerDownload(r.data, o.filename);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const downloadLocal = async (b) => {
    try {
      toast.info('Download startet…');
      const r = await superAdminApi.backupLocalDownload(b.filename);
      triggerDownload(r.data, b.filename);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  // Poll solange ein Lauf läuft
  useEffect(() => {
    if (status?.status === 'running') {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const r = await superAdminApi.backupStatus();
        setStatus(r.data.run);
        if (r.data.run?.status !== 'running') { clearInterval(pollRef.current); loadList(); setRunning(false); }
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [status?.status]);

  // Poll solange ein Gesamt-System-Lauf läuft
  useEffect(() => {
    if (fullStatus?.status === 'running') {
      clearInterval(fullPollRef.current);
      fullPollRef.current = setInterval(async () => {
        try {
          const r = await superAdminApi.backupFullStatus();
          setFullStatus(r.data.run);
          if (r.data.run?.status !== 'running') {
            clearInterval(fullPollRef.current);
            superAdminApi.backupFullList().then((x) => setFullBundles(x.data.items || [])).catch(() => {});
          }
        } catch { /* ignore */ }
      }, 5000);
    }
    return () => clearInterval(fullPollRef.current);
  }, [fullStatus?.status]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.secret_key) delete payload.secret_key;
      await superAdminApi.saveBackupConfig(payload);
      toast.success('Backup-Konfiguration gespeichert');
      loadConfig();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const payload = {};
      ['endpoint', 'access_key', 'secret_key', 'bucket', 'region'].forEach((k) => { if (form[k]) payload[k] = form[k]; });
      const r = await superAdminApi.backupTest(payload);
      if (r.data.ok) toast.success('Verbindung erfolgreich');
      else toast.error(`Verbindung fehlgeschlagen: ${r.data.error || 'unbekannt'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test fehlgeschlagen'); }
    finally { setTesting(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await superAdminApi.backupRun({ db_only: form.db_only });
      toast.success(r?.data?.cloud_ready
        ? 'Plattform-Backup gestartet (inkl. Infomaniak-Upload)'
        : 'Lokales Plattform-Backup gestartet (ohne Cloud-Upload)');
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Start fehlgeschlagen'); setRunning(false); }
  };

  const del = async (key) => {
    if (!window.confirm(`Backup wirklich löschen?\n${key}`)) return;
    try { await superAdminApi.backupDeleteObject(key); toast.success('Gelöscht'); loadList(); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const isRunning = status?.status === 'running' || running;

  return (
    <div data-testid="sa-backup" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Plattform-Backup</h1>
        <p className="mt-1 text-sm text-slate-600">
          Ein zentrales Infomaniak-Ziel sichert <strong>alle Mandanten</strong> der Plattform.
          Jeder Mandant kann zusätzlich sein eigenes Backup im Studio-Admin konfigurieren.
        </p>
      </div>

      {cfg?.is_preview && (
        <div data-testid="sa-backup-preview-note" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Vorschau-Umgebung erkannt — Läufe erstellen Backups, laden sie aber <strong>nicht</strong> zu Infomaniak hoch (Schutz vor Test-Daten in der Produktion).</span>
        </div>
      )}

      {/* Konfiguration */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-5 flex items-center gap-2">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-700"><DatabaseBackup className="h-4.5 w-4.5" /></div>
          <div>
            <h2 className="text-lg font-medium text-slate-900">Infomaniak Swiss Backup (S3)</h2>
            <p className="text-xs text-slate-500">Plattformweite Zugangsdaten — gelten für alle Mandanten.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Endpoint</span>
            <input data-testid="sa-backup-endpoint" className={inputCls} placeholder="https://s3.pub1.infomaniak.cloud" value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Bucket</span>
            <input data-testid="sa-backup-bucket" className={inputCls} placeholder="mein-bucket" value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Region</span>
            <input data-testid="sa-backup-region" className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Access-Key</span>
            <input data-testid="sa-backup-accesskey" className={inputCls} value={form.access_key} onChange={(e) => setForm({ ...form, access_key: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">
              Secret-Key {cfg?.has_secret_key && <span className="text-slate-500">(gesetzt)</span>}
            </span>
            <input data-testid="sa-backup-secretkey" className={inputCls} type="password" placeholder={cfg?.has_secret_key ? 'Leer lassen = unverändert' : 'Secret-Key eingeben'} value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Prefix (Ordner im Bucket)</span>
            <input data-testid="sa-backup-prefix" className={inputCls} value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Aufbewahrung (Tage)</span>
            <input data-testid="sa-backup-retention" type="number" min="1" className={inputCls} value={form.retention_days} onChange={(e) => setForm({ ...form, retention_days: parseInt(e.target.value || '30', 10) })} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Benachrichtigungs-E-Mail (optional)</span>
            <input data-testid="sa-backup-notify" className={inputCls} placeholder="admin@invictax.ch" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} />
          </label>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
          <label className="flex items-center gap-3">
            <input data-testid="sa-backup-enabled" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 bg-slate-100 text-indigo-500" />
            <span className="text-sm text-slate-700">Plattform-Backup aktiviert</span>
          </label>
          <label className="flex items-center gap-3">
            <input data-testid="sa-backup-dbonly" type="checkbox" checked={form.db_only} onChange={(e) => setForm({ ...form, db_only: e.target.checked })} className="h-4 w-4 rounded border-slate-300 bg-slate-100 text-indigo-500" />
            <span className="text-sm text-slate-700">Nur Datenbank (ohne Upload-Dateien)</span>
          </label>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button data-testid="sa-backup-save-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Speichert…' : 'Speichern'}
          </button>
          <button data-testid="sa-backup-test-btn" onClick={test} disabled={testing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <PlugZap className="h-4 w-4" /> {testing ? 'Teste…' : 'Verbindung testen'}
          </button>
          <button data-testid="sa-backup-run-btn" onClick={run} disabled={isRunning}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50">
            <Play className="h-4 w-4" /> {isRunning ? 'Backup läuft…' : 'Backup jetzt starten'}
          </button>
        </div>
        {!cfg?.enabled && (
          <p data-testid="sa-backup-local-hint" className="mt-2 text-xs text-slate-500">
            Ohne aktiviertes Infomaniak-Ziel wird je Mandant ein <strong>lokales</strong> Backup auf dem Server erstellt (unten abrufbar). Für dauerhafte Cloud-Sicherung oben die Zugangsdaten hinterlegen und aktivieren.
          </p>
        )}
      </div>

      {/* Letzter Lauf */}
      {status && (
        <div data-testid="sa-backup-status" className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-slate-900">Letzter Lauf</h2>
            <button data-testid="sa-backup-refresh-status" onClick={() => { loadStatus(); loadList(); }} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800">
              <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
              status.status === 'success' ? 'bg-emerald-100 text-emerald-700'
                : status.status === 'partial' ? 'bg-amber-100 text-amber-700'
                : status.status === 'running' ? 'bg-indigo-100 text-indigo-700'
                : 'bg-rose-100 text-rose-700'}`}>
              {status.status === 'running' ? <RefreshCw className="h-3 w-3 animate-spin" /> : status.status === 'success' ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              {status.status}
            </span>
            <span className="text-slate-600">Gestartet: {status.started_at ? new Date(status.started_at).toLocaleString('de-CH') : '—'}</span>
            {status.tenants_total != null && <span className="text-slate-600">Mandanten: {status.tenants_total}</span>}
            {status.cloud_ok != null && <span className="text-emerald-700">Cloud OK: {status.cloud_ok}</span>}
            {status.cloud_failed != null && status.cloud_failed > 0 && <span className="text-rose-700">Fehler: {status.cloud_failed}</span>}
          </div>
          {Array.isArray(status.results) && status.results.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {status.results.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs">
                  <span className="truncate font-medium text-slate-700">{r.name || r.slug}</span>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="text-slate-500">
                      {r.backup?.status === 'success'
                        ? `✓ lokal gesichert${r.backup?.size_mb != null ? ` · ${r.backup.size_mb} MB` : ''}`
                        : (r.error ? `Fehler: ${r.error}` : '—')}
                      {r.cloud?.ok ? ' · ☁️ Cloud' : (r.cloud?.skipped ? ' · nur lokal' : '')}
                    </span>
                    {r.backup?.filename && (
                      <button data-testid={`sa-backup-run-dl-${r.slug}`} onClick={() => downloadLocal({ filename: r.backup.filename })}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-indigo-600 hover:bg-indigo-50">
                        <Download className="h-3.5 w-3.5" /> Herunterladen
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cloud-Backups */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <HardDrive className="h-4.5 w-4.5 text-slate-500" />
          <h2 className="text-lg font-medium text-slate-900">Backups auf Infomaniak ({objects.length})</h2>
        </div>
        {objects.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Plattform-Backups vorhanden.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {objects.map((o) => (
              <div key={o.key} data-testid={`sa-backup-obj-${o.filename}`} className="flex items-center justify-between py-2.5">
                <div>
                  <div className="text-sm font-medium text-slate-800">{o.filename}</div>
                  <div className="text-xs text-slate-500">{o.size_mb} MB · {o.last_modified ? new Date(o.last_modified).toLocaleString('de-CH') : '—'}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`sa-backup-dl-${o.filename}`} onClick={() => downloadCloud(o)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50">
                    <Download className="h-3.5 w-3.5" /> Herunterladen
                  </button>
                  <button data-testid={`sa-backup-del-${o.filename}`} onClick={() => del(o.key)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-3.5 w-3.5" /> Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lokale Backups pro Mandant */}
      <div data-testid="sa-backup-local" className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Server className="h-4.5 w-4.5 text-slate-500" />
            <h2 className="text-lg font-medium text-slate-900">Lokale Backups pro Mandant</h2>
          </div>
          <div className="flex items-center gap-2">
            {uploading && (
              <span data-testid="sa-backup-upload-progress" className="text-xs text-slate-500">
                {uploadName} · {uploadPct}%
              </span>
            )}
            <input ref={fileInputRef} type="file" accept=".tar.gz,.gz,application/gzip" className="hidden"
              onChange={(e) => handleUploadFile(e.target.files?.[0])} data-testid="sa-backup-upload-input" />
            <button data-testid="sa-backup-upload-btn" disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
              <Upload className="h-3.5 w-3.5" /> {uploading ? `Lädt hoch… ${uploadPct}%` : 'Backup hochladen'}
            </button>
          </div>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Täglich automatisch erstellte Sicherungen auf dem Server (unabhängig von Infomaniak).
          Hinweis: lokale Backups können bei einem Deploy/Neustart verloren gehen — für dauerhafte Sicherung Infomaniak aktivieren.
          Über <strong>„Backup hochladen"</strong> kannst du eine heruntergeladene <span className="font-mono">.tar.gz</span>-Datei eines Mandanten wieder einspielen (Dateiname muss mit dem Studio-Kürzel beginnen).
        </p>
        {localTenants.length === 0 ? (
          <p className="text-sm text-slate-500">Keine lokalen Backups gefunden.</p>
        ) : (
          <div className="space-y-5">
            {localTenants.map((t) => (
              <div key={t.slug} data-testid={`sa-backup-local-tenant-${t.slug}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{t.name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{t.count}</span>
                </div>
                {t.backups.length === 0 ? (
                  <p className="pl-1 text-xs text-slate-400">Noch keine lokalen Backups.</p>
                ) : (
                  <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                    {t.backups.map((b) => (
                      <div key={b.filename} data-testid={`sa-backup-local-item-${b.filename}`} className="flex items-center justify-between px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="truncate font-mono text-xs text-slate-800">{b.filename}</div>
                          <div className="text-xs text-slate-500">{b.size_mb} MB · {b.created_at ? new Date(b.created_at).toLocaleString('de-CH') : '—'}</div>
                        </div>
                        <div className="ml-3 flex shrink-0 items-center gap-1">
                          <button data-testid={`sa-backup-local-dl-${b.filename}`} onClick={() => downloadLocal(b)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50">
                            <Download className="h-3.5 w-3.5" /> Herunterladen
                          </button>
                          <button data-testid={`sa-backup-local-restore-${b.filename}`} onClick={() => { setRestoreTgt({ ...b, name: t.name, slug: t.slug }); setRestoreConfirm(''); }} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-amber-700 hover:bg-amber-50">
                            <RotateCcw className="h-3.5 w-3.5" /> Wiederherstellen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Gesamt-System-Paket (alle Mandanten + Plattform) */}
      <div data-testid="sa-backup-full" className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-700"><Boxes className="h-4.5 w-4.5" /></div>
            <div>
              <h2 className="text-lg font-medium text-slate-900">Komplettes System (alle Studios + Plattform)</h2>
              <p className="text-xs text-slate-500">EIN Paket mit allen Studio-Datenbanken, der Plattform-Ebene und allen hochgeladenen Dateien.</p>
            </div>
          </div>
          <button data-testid="sa-backup-full-run-btn" onClick={runFull} disabled={fullStatus?.status === 'running'}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
            {fullStatus?.status === 'running' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {fullStatus?.status === 'running' ? 'Läuft…' : 'Komplettes System sichern'}
          </button>
        </div>

        {fullStatus && (
          <div data-testid="sa-backup-full-status" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${
              fullStatus.status === 'success' ? 'bg-emerald-100 text-emerald-700'
                : fullStatus.status === 'running' ? 'bg-indigo-100 text-indigo-700'
                : fullStatus.status === 'error' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
              {fullStatus.status === 'running' ? <RefreshCw className="h-3 w-3 animate-spin" /> : fullStatus.status === 'success' ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              {fullStatus.op === 'restore' ? 'Wiederherstellung' : 'Backup'}: {fullStatus.status}
            </span>
            {fullStatus.finished_at && <span className="text-slate-500">{new Date(fullStatus.finished_at).toLocaleString('de-CH')}</span>}
            {fullStatus.dbs_restored != null && <span className="text-slate-600">{fullStatus.dbs_restored} DBs wiederhergestellt</span>}
            {fullStatus.error && <span className="text-rose-600">{fullStatus.error}</span>}
          </div>
        )}

        {fullBundles.length === 0 ? (
          <p className="text-sm text-slate-500">Noch kein Komplett-Paket erstellt. Mit „Komplettes System sichern" das erste erzeugen.</p>
        ) : (
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white">
            {fullBundles.map((b) => (
              <div key={b.filename} data-testid={`sa-backup-full-item-${b.filename}`} className="flex items-center justify-between px-3 py-2.5">
                <div className="min-w-0">
                  <div className="truncate font-mono text-xs text-slate-800">{b.filename}</div>
                  <div className="text-xs text-slate-500">{b.size_mb} MB · {b.created_at ? new Date(b.created_at).toLocaleString('de-CH') : '—'}</div>
                </div>
                <div className="ml-3 flex shrink-0 items-center gap-1">
                  <button data-testid={`sa-backup-full-dl-${b.filename}`} onClick={() => downloadFull(b)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50">
                    <Download className="h-3.5 w-3.5" /> Herunterladen
                  </button>
                  <button data-testid={`sa-backup-full-restore-${b.filename}`} onClick={() => { setFullRestoreTgt(b); setFullConfirm(''); }} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-rose-700 hover:bg-rose-50">
                    <RotateCcw className="h-3.5 w-3.5" /> Komplett wiederherstellen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-slate-500">
          Tipp: Ein heruntergeladenes Komplett-Paket (<span className="font-mono">full-system-…</span>) kannst du über „Backup hochladen" (unten) wieder einspielen.
        </p>
      </div>

      {fullRestoreTgt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sa-backup-full-restore-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-2 flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-base font-semibold">Komplettes System wiederherstellen</h3>
            </div>
            <p className="mb-3 text-sm text-slate-600">
              Du spielst <b>ALLE Studios + die Plattform-Ebene</b> aus <span className="font-mono text-xs">{fullRestoreTgt.filename}</span> ein.
              Dabei werden die <b>aktuellen Daten des gesamten Systems überschrieben</b>. Vorher wird je Datenbank automatisch ein Sicherungs-Backup erstellt.
            </p>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Zum Bestätigen exakt eingeben: <b>{FULL_CONFIRM}</b></label>
            <input data-testid="sa-backup-full-restore-confirm" className={inputCls} value={fullConfirm} onChange={(e) => setFullConfirm(e.target.value)} placeholder={FULL_CONFIRM} autoFocus />
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="sa-backup-full-restore-cancel" onClick={() => { setFullRestoreTgt(null); setFullConfirm(''); }} className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Abbrechen</button>
              <button data-testid="sa-backup-full-restore-confirm-btn" onClick={doFullRestore}
                disabled={fullRestoring || fullConfirm.trim().toUpperCase() !== FULL_CONFIRM}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-50">
                <RotateCcw className="h-4 w-4" /> {fullRestoring ? 'Starte…' : 'Komplett wiederherstellen'}
              </button>
            </div>
          </div>
        </div>
      )}


      {restoreTgt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sa-backup-restore-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-2 flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-base font-semibold">Backup wiederherstellen</h3>
            </div>
            <p className="mb-3 text-sm text-slate-600">
              Du stellst <b>{restoreTgt.name}</b> aus <span className="font-mono text-xs">{restoreTgt.filename}</span> wieder her.
              Dabei werden die <b>aktuellen Daten dieses Studios überschrieben</b>. Vorher wird automatisch ein Sicherungs-Backup erstellt.
            </p>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Zum Bestätigen Studionamen eingeben: <b>{restoreTgt.name}</b></label>
            <input data-testid="sa-backup-restore-confirm" className={inputCls} value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)} placeholder={restoreTgt.name} autoFocus />
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="sa-backup-restore-cancel" onClick={() => { setRestoreTgt(null); setRestoreConfirm(''); }} className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Abbrechen</button>
              <button data-testid="sa-backup-restore-confirm-btn" onClick={doRestore}
                disabled={restoring || restoreConfirm.trim().toLowerCase() !== String(restoreTgt.name).toLowerCase()}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50">
                <RotateCcw className="h-4 w-4" /> {restoring ? 'Stelle wieder her…' : 'Jetzt wiederherstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
